import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'office',
    allowedModules: [],
    hourlyWage: '',
  });

  const ALL_MODULES = [
    { value: 'dashboard', label: 'Dashboard', description: 'Main overview' },
    { value: 'inventory', label: 'Inventory', description: 'Vehicle inventory management' },
    { value: 'inventory_limited', label: 'Inventory (Limited)', description: 'View inventory without financials/title info - for Tony' },
    { value: 'transport', label: 'Transport', description: 'Vehicle transport coordination' },
    { value: 'recon', label: 'Service/Recon', description: 'Repair orders & reconditioning' },
    { value: 'partsroom', label: 'Parts Room', description: 'Parts inventory & ordering' },
    { value: 'detail', label: 'Detail', description: 'Detail task queue' },
    { value: 'accounting', label: 'Accounting', description: 'Bills, expenses, payments' },
    { value: 'titles', label: 'Titles', description: 'Title processing & tracking' },
    { value: 'timeclock', label: 'Time Clock', description: 'Employee time tracking' },
    { value: 'analytics', label: 'Analytics', description: 'Reports & data analysis' },
    { value: 'admintasks', label: 'Admin Tasks', description: 'Administrative task management' },
    { value: 'dealdesk', label: 'Deal Desk', description: 'Deal processing' },
    { value: 'reports', label: 'Reports', description: 'Generate reports' },
    { value: 'vendors', label: 'Vendors', description: 'Vendor management' },
    { value: 'expenses', label: 'Expenses', description: 'Expense tracking' },
    { value: 'buyerdash', label: 'Buyer Dashboard', description: 'Buying guide & budget' },
    { value: 'arb', label: 'ARB', description: 'Arbitration case management' },
    { value: 'reconworkflow', label: 'Recon Workflow', description: 'Card-based workflow with vehicle photos for shop staff' },
    { value: 'dealdecklite', label: 'Deal Desk Lite', description: 'Simplified sale entry with pre-sale calculator' },
    { value: 'bhph', label: 'BHPH Portfolio', description: 'Buy-here-pay-here contracts & servicing (siloed)' },
  ];

  const VALID_ROLES = [
    { value: 'admin', label: 'Admin', description: 'Full system access', color: 'amber' },
    { value: 'office', label: 'Office', description: 'Finance, titles, inventory', color: 'blue' },
    { value: 'shop', label: 'Shop', description: 'Recon, inspections, parts', color: 'green' },
    { value: 'detail', label: 'Detail', description: 'Task queue only', color: 'purple' },
    { value: 'inventory_viewer', label: 'Inventory Viewer', description: 'View inventory, recon, parts (no financials)', color: 'blue' },
    { value: 'buyer', label: 'Buyer', description: 'Buying, transport, inventory', color: 'green' },
  ];
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditing) {
      const fetchUser = async () => {
        try {
          const user = await api.getUser(id);
          setFormData({
            name: user.name,
            email: user.email,
            password: '',
            role: user.role,
            allowedModules: user.allowedModules || [],
            hourlyWage: user.hourlyWage ? String(user.hourlyWage) : '',
          });
        } catch (error) {
          console.error('Failed to fetch user:', error);
        }
      };
      fetchUser();
    }
  }, [id, isEditing]);

  const Icon = ({ name, size = 16, className = '' }) => {
    const paths = {
      arrow: <><polyline points="15 18 9 12 15 6" /></>,
      home: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>,
      user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
      mail: <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>,
      lock: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
      shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
    };
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{paths[name]}</svg>;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      setLoading(false);
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    if (!isEditing && !formData.password) {
      setError('Password is required');
      setLoading(false);
      return;
    }
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    if (formData.allowedModules.length === 0) {
      setError('Please select at least one module');
      setLoading(false);
      return;
    }

    try {
      if (isEditing) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        if (updateData.hourlyWage === '') {
          delete updateData.hourlyWage;
        }
        await api.updateUser(id, updateData);
      } else {
        const createData = { ...formData };
        if (createData.hourlyWage === '') {
          delete createData.hourlyWage;
        }
        await api.createUser(createData);
      }
      navigate('/admin/users');
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb]" style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/users" className="w-10 h-10 rounded-lg bg-[#f1f5f9] flex items-center justify-center hover:bg-[#e2e8f0] transition-colors">
                <Icon name="arrow" size={20} className="text-[#64748b]" />
              </Link>
              <div>
                <h1 className="text-xl font-semibold text-[#0f172a]">
                  {isEditing ? 'Edit User' : 'Create User'}
                </h1>
                <p className="text-sm text-[#64748b]">
                  {isEditing ? 'Update user account details' : 'Add a new user account'}
                </p>
              </div>
            </div>
            <Link to="/" className="flex items-center gap-2 h-10 px-4 bg-[#f1f5f9] text-[#475569] rounded-lg hover:bg-[#e2e8f0] transition-colors">
              <Icon name="home" size={16} />
              <span className="text-sm font-medium">Back to App</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e2e8f0]">
            <h2 className="text-lg font-semibold text-[#0f172a]">User Details</h2>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-6 p-4 bg-[#fef2f2] border border-[#fecaca] rounded-lg">
              <p className="text-sm text-[#dc2626]">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="user" size={16} className="text-[#64748b]" />
                  Name
                </div>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="w-full h-12 px-4 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/20 focus:border-[#3b82f6]"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="mail" size={16} className="text-[#64748b]" />
                  Email
                </div>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className="w-full h-12 px-4 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/20 focus:border-[#3b82f6]"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="lock" size={16} className="text-[#64748b]" />
                  Password
                  {isEditing && <span className="text-xs text-[#64748b] font-normal">(leave blank to keep current)</span>}
                </div>
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isEditing ? '••••••••' : 'Minimum 6 characters'}
                className="w-full h-12 px-4 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/20 focus:border-[#3b82f6]"
              />
            </div>

            {/* Hourly Wage */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="user" size={16} className="text-[#64748b]" />
                  Hourly Wage
                  <span className="text-xs text-[#64748b] font-normal">(used for payroll and week summaries)</span>
                </div>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b]">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  name="hourlyWage"
                  value={formData.hourlyWage}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="w-full h-12 pl-8 pr-4 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3b82f6]/20 focus:border-[#3b82f6]"
                />
              </div>
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="shield" size={16} className="text-[#64748b]" />
                  Role
                </div>
              </label>
              <div className="grid grid-cols-2 gap-4">
                {VALID_ROLES.map(role => {
                  const colorMap = {
                    amber: { border: 'border-[#f59e0b]', bg: 'bg-[#fffbeb]', active: 'bg-[#f59e0b]' },
                    blue: { border: 'border-[#3b82f6]', bg: 'bg-[#eff6ff]', active: 'bg-[#3b82f6]' },
                    green: { border: 'border-[#22c55e]', bg: 'bg-[#f0fdf4]', active: 'bg-[#22c55e]' },
                    purple: { border: 'border-[#8b5cf6]', bg: 'bg-[#faf5ff]', active: 'bg-[#8b5cf6]' },
                  };
                  const colors = colorMap[role.color];
                  const isSelected = formData.role === role.value;
                  
                  return (
                    <label
                      key={role.value}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? `${colors.border} ${colors.bg}`
                          : 'border-[#e2e8f0] hover:border-[#94a3b8]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role.value}
                        checked={isSelected}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected ? `${colors.active} text-white` : 'bg-[#f1f5f9] text-[#64748b]'
                      }`}>
                        <Icon name={role.value === 'admin' ? 'shield' : 'user'} size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-[#0f172a]">{role.label}</p>
                        <p className="text-xs text-[#64748b]">{role.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Module Access */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="shield" size={16} className="text-[#64748b]" />
                  Module Access
                  <span className="text-xs text-[#dc2626]">*</span>
                </div>
              </label>
              <p className="text-xs text-[#64748b] mb-3">Select which modules this user can access</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ALL_MODULES.map(module => {
                  const isSelected = formData.allowedModules.includes(module.value);
                  return (
                    <label
                      key={module.value}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-[#3b82f6] bg-[#eff6ff]'
                          : 'border-[#e2e8f0] hover:border-[#94a3b8]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({
                              ...prev,
                              allowedModules: [...prev.allowedModules, module.value]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              allowedModules: prev.allowedModules.filter(m => m !== module.value)
                            }));
                          }
                        }}
                        className="w-4 h-4 text-[#3b82f6] rounded border-[#d1d5db] focus:ring-[#3b82f6]"
                      />
                      <div>
                        <p className="text-sm font-medium text-[#0f172a]">{module.label}</p>
                        <p className="text-xs text-[#64748b]">{module.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, allowedModules: ALL_MODULES.map(m => m.value) }))}
                  className="text-xs text-[#3b82f6] hover:underline"
                >
                  Select All
                </button>
                <span className="text-[#d1d5db]">|</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, allowedModules: [] }))}
                  className="text-xs text-[#64748b] hover:underline"
                >
                  Clear All
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#e2e8f0]">
              <Link
                to="/admin/users"
                className="h-11 px-6 border border-[#e2e8f0] rounded-lg font-medium flex items-center hover:bg-[#f8f9fb] transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="h-11 px-6 bg-[#3b82f6] text-white rounded-lg font-medium hover:bg-[#2563eb] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : isEditing ? 'Update User' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default UserForm;
